import clsx from 'clsx';
import { ReactElement, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CookieConsentBanner, ErrorPage } from '@/features';
import { PageType } from '@/types/page';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@/utils';
import { Desktop } from '@/utils/responsive';
import { createClose } from '@/assets/icons';
import { Icon } from '@/components';
import isServer from '@/utils/isServer';
import { Footer } from '../footer';
import { Header } from '../header';
import { SecondTierNav } from '../second-tier-nav';

const SECURITY_NOTICE_STORAGE_KEY = 'security-notice-data';
const SECURITY_NOTICE_EXPIRATION_TIME = 2 * 24 * 60 * 60 * 1000; // 2 days

// Initialize visibility state by checking localStorage
const getInitialVisibility = (): boolean => {
  if (isServer()) return true;

  try {
    const storedData = JSON.parse(
      localStorage.getItem(SECURITY_NOTICE_STORAGE_KEY) || '{}'
    );
    const now = new Date().getTime();

    if (storedData.hidden && storedData.expiresAt > now) {
      return false; // Notice should be hidden
    }
  } catch (e) {
    console.error('Error checking security notice status:', e);
  }

  return true; // Default to visible
};

export type SiteLayoutProps = StyleProps & {
  children: ReactElement;

  /**
   * The page type the layout is being rendered on.
   * Optional unless you need the visual style of the layout to
   * change based on the selected page.
   */
  pageType?: PageType;
};

const ROOT = makeRootClassName('SiteLayout');
const el = makeElementClassNameFactory(ROOT);

const DEFAULT_PROPS = {} as const;

function SiteLayout(props: SiteLayoutProps): ReactElement {
  const p = { ...DEFAULT_PROPS, ...props };
  const pageType = p.pageType;

  // Use lazy initializer to check localStorage during first render
  const [isSecurityNoticeVisible, setIsSecurityNoticeVisible] =
    useState(getInitialVisibility);

  const hideSecurityNotice = () => {
    if (!isServer()) {
      try {
        // Set expiration to 2 days from now (for production)
        const expiryTime =
          new Date().getTime() + SECURITY_NOTICE_EXPIRATION_TIME;

        localStorage.setItem(
          SECURITY_NOTICE_STORAGE_KEY,
          JSON.stringify({
            hidden: true,
            expiresAt: expiryTime,
          })
        );

        setIsSecurityNoticeVisible(false);
      } catch (e) {
        console.error('Error saving security notice status:', e);
      }
    }
  };

  return (
    <div className={clsx(ROOT, p.className)}>
      <ErrorBoundary FallbackComponent={ErrorPage}>
        <Desktop>
          <SecondTierNav pageType={p.pageType} />
        </Desktop>
        {isSecurityNoticeVisible && (
          <div className={el`security-notice`}>
            <div className={el`security-notice-content`}>
              We&apos;ll never ask for payment, passwords, or personal info you
              don&apos;t choose to share. Stay alert.
            </div>
            <button
              onClick={hideSecurityNotice}
              className={el`security-notice-close`}
              aria-label="Close security notice"
            >
              <Icon content={createClose} size="small" />
            </button>
          </div>
        )}
        <Header pageType={p.pageType} />
        <div
          className={clsx(el`content`, {
            'is-home': pageType === PageType.HOME,
          })}
        >
          {p.children}
        </div>
        <Footer pageType={p.pageType} />
        <CookieConsentBanner />
      </ErrorBoundary>
    </div>
  );
}

export const getLayout = (
  page: ReactElement,
  pageType?: PageType
): ReactElement => <SiteLayout pageType={pageType}>{page}</SiteLayout>;

export default SiteLayout;
